/* ==========================================================================
   Kiro — Four Play quad: "exploded tower" scroll animation
   File: assets/kiro-quad-story.js   (loaded by sections/kiro-quad-story.liquid)

   Enhances the product page story section (section.kp-story) that the theme
   already pins while scrolling. The theme writes data-progress (0 → 1) on
   that section; this script reads it, smooths it and animates the quad:

     0.00–0.32  the upright tower turns to a diagonal hero angle and grows;
                its four parts separate along the tower's axis (centre seam
                first, then the two outer seams)
     0.32–0.62  the exploded view keeps breathing and turning slowly
     0.62–0.94  parts close back together as the tower rotates upright
     0.94–1.00  locks into one; a glint runs over the caps
     throughout a soft halo behind the product drifts through the four shades

   Configuration is read from <script type="application/json"
   id="kiro-quad-story-config"> (written by the section). No edits needed here.
   ========================================================================== */
(function () {
  'use strict';

  if (window.KiroQuadStory) return;                           // loaded twice → keep the first

  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };   // easeInOutCubic
  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };
  var win = function (p, a, b) { return clamp((p - a) / (b - a), 0, 1); };

  // ---- Product image ---------------------------------------------------------
  // Ultimate Quad product shot (764×1000). The CSS crops the tower out of it,
  // so this must stay this exact image.
  var DEFAULT_SRC = 'https://cdn.shopify.com/s/files/1/0089/0893/1152/files/1251_prod_featured_img.jpg?width=764';

  // ---- Geometry (360×360 scene, rig origin at its centre) -------------------
  var TOWER_H = 340, TOWER_W = TOWER_H * 115 / 870;
  var ROWS = [0, 348, 514, 680, 870];                          // ring splits in the 870px source tower
  var TILT = 34;                                               // hero angle, degrees clockwise
  var GAP = 24;                                                // exploded gap between parts
  var OPEN_SCALE = 1.06;                                       // product grows a little as it opens
  var SEAM_DELAY = [0.18, 0, 0.18];                            // centre seam opens first

  var DEFAULT_SHADES = ['#874a40', '#c2706d', '#b5795c', '#a9505a'];  // brown, blush pink, light brown, deep nude pink

  function rgb(hex) { var n = parseInt(String(hex).replace('#', ''), 16); return [n >> 16, (n >> 8) & 255, n & 255]; }
  function mixRGB(a, b, t) { return a.map(function (v, i) { return Math.round(lerp(v, b[i], t)); }); }

  var current = null;                                          // the running instance

  function init(opts) {
    opts = opts || {};
    var section = opts.section || document.querySelector('section.kp-story');
    if (!section) return null;
    var art = section.querySelector('.kp-story-art');
    if (!art) return null;

    if (current) current.destroy();

    var src = opts.src || DEFAULT_SRC;
    var shades = DEFAULT_SHADES.map(function (d, i) {           // any missing or invalid colour falls back
      var s = opts.shades && opts.shades[i];
      return rgb(/^#?[0-9a-f]{6}$/i.test(String(s || '').trim()) ? String(s).trim() : d);
    });

    // Story copy (5 points). Only replaced when provided.
    if (opts.copy && opts.copy.length) {
      section.querySelectorAll('[data-kp-reason]').forEach(function (a, i) {
        var c = opts.copy[i];
        if (!c) return;
        var h = a.querySelector('h2, h3'), p = a.querySelector('p');
        if (h && c.title) h.textContent = c.title;
        if (p && c.body) p.textContent = c.body;
      });
    }

    var old = art.querySelector('.kq-stage');
    if (old) old.remove();

    var stage = document.createElement('div');
    stage.className = 'kq-stage';
    stage.setAttribute('aria-hidden', 'true');
    stage.innerHTML =
      '<div class="kq-scene">' +
        '<div class="kq-halo"></div>' +
        '<div class="kq-floor"></div>' +
        '<div class="kq-shadow"><div class="kq-rig"></div></div>' +
      '</div>';
    var scene = stage.querySelector('.kq-scene');
    var halo = stage.querySelector('.kq-halo');
    var floor = stage.querySelector('.kq-floor');
    var rig = stage.querySelector('.kq-rig');

    // Parts, laid out along the rig's local Y axis (centre of the tower = 0)
    var top = -TOWER_H / 2;
    var pieces = [0, 1, 2, 3].map(function (i) {
      var h = TOWER_H * (ROWS[i + 1] - ROWS[i]) / 870;
      var el = document.createElement('div');
      el.className = 'kq-piece';
      el.setAttribute('data-i', i);
      el.style.width = TOWER_W + 'px';
      el.style.height = h + 'px';
      el.style.backgroundImage = 'url("' + src + '")';
      rig.appendChild(el);
      var pc = { el: el, h: h, y: top + h / 2 };
      top += h;
      return pc;
    });

    art.appendChild(stage);
    section.classList.add('kq-on');

    function fit() {
      var r = stage.getBoundingClientRect();
      scene.style.setProperty('--kq-fit', (Math.min(r.width, r.height) / 360 || 1).toFixed(4));
    }
    fit();
    var ro = 'ResizeObserver' in window ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(stage); else window.addEventListener('resize', fit);

    var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

    function render(p) {
      if (reduced.matches) p = 0.5;                           // reduced motion: one still, opened view

      var open = p < 0.5 ? ease(win(p, 0.06, 0.32)) : 1 - ease(win(p, 0.62, 0.94));
      var breath = 1 + 0.12 * Math.sin(Math.PI * win(p, 0.32, 0.62));

      var angle = p < 0.5
        ? TILT * ease(win(p, 0, 0.32)) + 4 * win(p, 0.32, 0.5)
        : (TILT + 4) * (1 - ease(win(p, 0.5, 0.94)));

      var scale = lerp(1, OPEN_SCALE, Math.min(1, angle / TILT)) *
        (1 + 0.012 * Math.exp(-Math.pow((p - 0.975) / 0.012, 2)));
      var lift = lerp(6, -6, p);

      rig.style.transform = 'translate3d(0,' + lift.toFixed(2) + 'px,0) rotate(' + angle.toFixed(3) + 'deg) scale(' + scale.toFixed(4) + ')';

      var gaps = SEAM_DELAY.map(function (d) { return GAP * breath * easeOut(clamp((open - d) / (1 - d), 0, 1)); });
      var total = gaps[0] + gaps[1] + gaps[2];
      var acc = 0;
      pieces.forEach(function (pc, i) {
        if (i > 0) acc += gaps[i - 1];
        var y = pc.y + acc - total / 2;
        pc.el.style.transform = 'translate3d(' + (-TOWER_W / 2).toFixed(2) + 'px,' + (y - pc.h / 2).toFixed(2) + 'px,0)';
      });

      var t = clamp(p, 0, 1) * (shades.length - 1), k = Math.min(shades.length - 2, Math.floor(t));
      var c = mixRGB(shades[k], shades[k + 1], ease(t - k));
      halo.style.setProperty('--kq-halo', 'rgba(' + c.join(',') + ',0.30)');
      halo.style.setProperty('--kq-halo-o', (0.55 + 0.45 * Math.max(open, win(p, 0, 0.1) * 0.6)).toFixed(3));

      floor.style.setProperty('--kq-floor', (1 - Math.min(1, angle / 12)).toFixed(3));
      var glint = p < 0.5 ? win(p, 0, 0.1) : win(p, 0.9, 1);
      scene.style.setProperty('--kq-glint', lerp(-60, 60, ease(glint)).toFixed(1) + '%');
    }

    function readProgress() {
      var v = parseFloat(section.getAttribute('data-progress'));
      if (!isNaN(v)) return clamp(v, 0, 1);
      // Fallback if the theme stops writing data-progress
      var pin = section.querySelector('.kp-story-pin');
      var rect = section.getBoundingClientRect();
      var travel = Math.max(1, section.offsetHeight - (pin ? pin.offsetHeight : 0));
      var pinTop = pin ? parseFloat(getComputedStyle(pin).top) || 0 : 0;
      return clamp((pinTop - rect.top) / travel, 0, 1);
    }

    // Inertia: drawn progress eases toward scroll progress (frame-rate independent)
    var shown = readProgress(), drawn = -1, raf = 0, visible = true, lastT = 0, dead = false;
    function tick(now) {
      raf = 0;
      if (dead) return;
      var target = readProgress();
      var dt = lastT ? Math.min(64, now - lastT) : 16;
      lastT = now;
      shown += (target - shown) * (1 - Math.exp(-dt / 140));
      if (Math.abs(target - shown) < 0.0004) shown = target;
      if (Math.abs(shown - drawn) > 0.0002) { drawn = shown; render(shown); }
      if (visible) raf = requestAnimationFrame(tick); else lastT = 0;
    }

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !raf && !dead) raf = requestAnimationFrame(tick);
      }, { rootMargin: '200px' });
      io.observe(section);
    }
    render(shown);
    raf = requestAnimationFrame(tick);

    current = {
      section: section,
      stage: stage,
      render: render,
      destroy: function () {
        dead = true;
        if (raf) cancelAnimationFrame(raf);
        if (io) io.disconnect();
        if (ro) ro.disconnect(); else window.removeEventListener('resize', fit);
        if (stage.parentNode) stage.parentNode.removeChild(stage);
        section.classList.remove('kq-on');
        current = null;
      }
    };
    return current;
  }

  // ---- Auto start -------------------------------------------------------------
  function readConfig() {
    var el = document.getElementById('kiro-quad-story-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; }
  }

  function boot() {
    var cfg = readConfig();
    if (!cfg || cfg.enabled === false) return;

    var start = function () {
      var section = document.querySelector('section.kp-story');
      if (!section) return;
      if (current && current.section === section && section.contains(current.stage)) return;
      init({ section: section, src: cfg.src, shades: cfg.shades, copy: cfg.copy });
    };
    start();

    // The theme can re-render the product page (e.g. when switching shades).
    // Re-attach whenever the story section is replaced or our stage is removed.
    var pending = 0;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = 0; start(); }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.KiroQuadStory = { init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
